import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { featureFlags } from '../../../../feature-flags';
import { trackingIds } from '../../../../tracking-ids';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { useCallIntelLocations } from '../../hooks/use-call-intel-locations';
import { FilterNotifications } from '../atoms/filter-notifications';
import { CallIntelFiltersProvider } from '../context/call-intel-filters.provider';
import { ChipBar } from '../molecules/chip-bar';
import { ContactTypeFilterChip } from '../molecules/contact-type-filter/contact-type-filter-chip';
import { ContactTypeFilterTray } from '../molecules/contact-type-filter/contact-type-filter-tray';
import { FilterTraySection } from '../molecules/filter-tray-section/filter-tray-section';
import { FilterTrayToggle } from '../molecules/filter-tray-section/filter-tray-toggle';
import { FollowUpReasonFilterTray } from '../molecules/follow-up-reason-filter-tray';
import { LocationFilterChip } from '../molecules/location-filter/location-filter-chip';
import { LocationFilterTray } from '../molecules/location-filter/location-filter-tray';
import { OfficeHoursFilterTray } from '../molecules/office-hours-filter-tray';
import { OfficeHoursResetBanner } from '../molecules/office-hours-reset-banner';
import { OfficeUserFilterChip } from '../molecules/office-user-filter/office-user-filter-chip';
import { OfficeUserFilterTray } from '../molecules/office-user-filter/office-user-filter-tray';
import { SearchFilter } from '../molecules/search-filter';
import { CallIntelSortFilter } from '../molecules/sort-filter';
import { TaskStatusFilterChip } from '../molecules/task-status-filter/task-status-filter-chip';
import { TaskStatusFilterTray } from '../molecules/task-status-filter/task-status-filter-tray';
import { TimePeriodFilterChip } from '../molecules/time-period-filter/time-period-filter-chip';
import { TimePeriodFilterTray } from '../molecules/time-period-filter/time-period-filter-tray';
import { FilterTray } from './filter-tray';

interface CallIntelFollowUpFiltersRootProps extends PropsWithChildren {
  isLoading?: boolean;
}

const CallIntelFollowUpFiltersRoot: FC<CallIntelFollowUpFiltersRootProps> = ({ isLoading, children }) => {
  return (
    <CallIntelFiltersProvider isLoading={isLoading}>
      <div css={styles.root}>{children}</div>
    </CallIntelFiltersProvider>
  );
};

interface CallIntelFollowUpFiltersProps {
  containerStyles?: Interpolation<Theme>;
  officeUsers?: Record<string, string>;
  trackingIdBase?: string;
  sortOption?: CallIntelTypes.CallIntelSortOption;
  onSortOptionChange?: (option: CallIntelTypes.CallIntelSortOption) => void;
  hideFilters?: (keyof CallIntelTypes.Filters)[];
  searchAction?: {
    show: boolean;
    searchValue: string;
    onChange: (value: string, debounced?: boolean) => void;
    trackingId?: string;
  };
}

const Filters: FC<CallIntelFollowUpFiltersProps> = ({
  containerStyles,
  officeUsers,
  trackingIdBase,
  sortOption = 'lastCallLatest',
  onSortOptionChange,
  hideFilters = [],
  searchAction,
}) => {
  const { isMultiLocation } = useCallIntelLocations();
  const { filters } = useActiveFilters();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { t } = useTranslation('call-intelligence');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [sortOptionTrayState, setSortOptionTrayState] = useState<CallIntelTypes.CallIntelSortOption>(sortOption);

  const expandableSearchModeProps = useMemo(
    () => ({ isExpanded: isSearchExpanded, onExpandStateChange: setIsSearchExpanded }),
    [isSearchExpanded, setIsSearchExpanded]
  );

  const hasStaffPerformanceAnalyticsAccess = useHasCallIntelAclAccess(
    [Permission.CALL_INTELLIGENCE_STAFF_PERFORMANCE_ANALYTICS],
    {
      locations: filters.locations,
      allowWeaveOverride: true,
      allowDemoOverride: true,
      requireAllLocations: true,
    }
  );

  const isOfficeHourFilterEnabled = getFeatureFlagValue(featureFlags.enableOfficeHourFilter);

  const shouldHideFilter = useCallback(
    (filterKey: keyof CallIntelTypes.Filters) => hideFilters?.includes(filterKey) ?? false,
    [hideFilters]
  );

  const handleTrayDismiss = () => {
    setIsSearchExpanded(false);
    setSortOptionTrayState(sortOption);
  };

  const handleApplyTrayFilters = () => {
    onSortOptionChange?.(sortOptionTrayState);
  };

  const handleFiltersReset = () => {
    searchAction?.onChange?.('', false);
    onSortOptionChange?.('lastCallLatest');
  };

  const renderChips = useMemo(() => {
    if (isSearchExpanded) {
      return [];
    }
    return [
      isMultiLocation && <LocationFilterChip key='location-filter-chip' />,
      <TimePeriodFilterChip key='time-period-filter-chip' />,
      <ContactTypeFilterChip key='contact-type-filter-chip' />,
      hasStaffPerformanceAnalyticsAccess && (
        <OfficeUserFilterChip key='office-user-filter-chip' officeUsers={officeUsers} />
      ),
      <TaskStatusFilterChip key='task-status-filter-chip' trackingId={trackingIdBase} />,
    ];
  }, [isSearchExpanded, isMultiLocation, hasStaffPerformanceAnalyticsAccess, officeUsers, trackingIdBase]);

  return (
    <>
      <div css={styles.filtersContainer}>
        <ChipBar
          maxVisibleChipsSmallMode={2}
          containerStyles={[styles.chipBar(isSearchExpanded), containerStyles]}
          extraRender={
            searchAction?.show ? (
              <SearchFilter
                minSearchLength={3}
                onChange={searchAction.onChange}
                value={searchAction.searchValue}
                trackingId={searchAction.trackingId}
                placeholder={t('Search Name or Phone Number')}
                wrapperStyles={styles.searchBar(isSearchExpanded)}
                expandableMode={expandableSearchModeProps}
              />
            ) : undefined
          }
        >
          {renderChips}
        </ChipBar>
      </div>
      <FilterTray onClearAll={handleFiltersReset} onDismiss={handleTrayDismiss} onApplyFilters={handleApplyTrayFilters}>
        {isMultiLocation && <LocationFilterTray />}
        <TimePeriodFilterTray />
        <ContactTypeFilterTray />
        {hasStaffPerformanceAnalyticsAccess && <OfficeUserFilterTray officeUsers={officeUsers} />}
        <TaskStatusFilterTray trackingIdBase={trackingIdBase} />
        {!shouldHideFilter('taskTypes') && <FollowUpReasonFilterTray />}
        {isOfficeHourFilterEnabled && <OfficeHoursFilterTray />}
        <FilterTraySection label={t('Sort By')} onClear={() => setSortOptionTrayState('lastCallLatest')}>
          <CallIntelSortFilter
            defaultSortOption='lastCallLatest'
            selectedSortOption={sortOptionTrayState}
            onSortChange={setSortOptionTrayState}
            showRadioOptions
            trackingId={trackingIds.callIntel.followUpSortFilter}
          />
        </FilterTraySection>
      </FilterTray>

      <FilterNotifications>
        <OfficeHoursResetBanner />
      </FilterNotifications>
    </>
  );
};

interface ActionsProps extends PropsWithChildren {
  showTrayFilters?: boolean;
  containerStyles?: Interpolation<Theme>;
}

const Actions: FC<ActionsProps> = ({ children, showTrayFilters = true, containerStyles }) => {
  const { isSmallMode } = useIsSmallMode();

  return (
    <div css={[styles.actionsContainer, containerStyles]}>
      {children}
      {showTrayFilters && !isSmallMode && <FilterTrayToggle css={styles.trayButton} />}
    </div>
  );
};

export const CallIntelFollowUpFilters = Object.assign(CallIntelFollowUpFiltersRoot, {
  Filters,
  Actions,
});

const styles = {
  root: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    align-items: center;
  `,
  filtersContainer: css`
    display: contents;
  `,

  actionsContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-left: auto;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      margin-left: 0;
    }
  `,
  chipBar: (isSearchExpanded: boolean) => css`
    width: ${isSearchExpanded ? '100%' : 'auto'};
    flex-wrap: wrap;
    display: contents;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      display: flex;
    }
  `,
  searchBar: (isSearchExpanded: boolean) => css`
    flex: ${isSearchExpanded ? 1 : 0};
  `,
  trayButton: css`
    padding: ${theme.spacing(0.5)};
    height: ${theme.spacing(4)};
    width: ${theme.spacing(4)};
  `,
};
